/**
 * Подтипы исхода «это не культура» для очереди разбора терминов хозяйства.
 *
 * Термин в списке культур хозяйства часто вообще не культура: это либо
 * СОСТОЯНИЕ ПОЛЯ («пар», «АМП», «под мелиорацией»), либо ТИП УГОДЬЯ («залежь»,
 * «сенокос», «сад»), либо СМЕСЬ культур («травосмесь»).
 *
 * Здесь остаётся ТОЛЬКО маппинг «вид подтипа → какой справочник показывать».
 * Сами значения приходят с сервера — `useFieldStates()` / `useLandCategories()`
 * (`./fieldCatalogs`). Копии списков в коде больше нет: справочник ведёт
 * агроном в БД, коды по определению совпадают с reference.* (нет 422 от API).
 */
import type { CodeLabeler } from './fieldCatalogs';
import type { NotACropKind } from './tenantCrops';

export interface CodeOption {
  code: string;
  label: string;
}

/** Человеческая подпись вида «не культуры». */
export const NOT_A_CROP_KIND_LABEL: Record<NotACropKind, string> = {
  field_state: 'Состояние поля',
  land_parcel: 'Тип угодья',
  crop_mix: 'Смесь культур',
};

/**
 * Какой справочник спрашивать для выбранного вида. `crop_mix` значения не
 * требует — смесь культур разбирается отдельно.
 */
export const CATALOG_FOR_KIND: Record<
  NotACropKind,
  'field_states' | 'land_categories' | null
> = {
  field_state: 'field_states',
  land_parcel: 'land_categories',
  crop_mix: null,
};

/** Нужно ли выбирать конкретное значение из справочника (шаг 2 диалога). */
export function needsCatalogCode(kind: NotACropKind | null): boolean {
  return kind != null && CATALOG_FOR_KIND[kind] != null;
}

/**
 * Подпись чипа для строки «не культура»: вид + конкретное значение, если
 * бэкенд его отдал («Тип угодья · Сенокос»), иначе только вид.
 *
 * Подписи значений приходят лукапами из загруженных справочников; пока они
 * грузятся — показываем один вид, кода на экране не будет.
 */
export function notACropLabel(
  item: {
    not_a_crop_kind?: NotACropKind | null;
    field_state_code?: string | null;
    land_category_code?: string | null;
  },
  labels?: {
    fieldState?: CodeLabeler;
    landCategory?: CodeLabeler;
  },
): string {
  const kind = item.not_a_crop_kind;
  if (!kind) return 'Не культура';
  const value =
    kind === 'field_state'
      ? (labels?.fieldState?.(item.field_state_code) ?? null)
      : kind === 'land_parcel'
        ? (labels?.landCategory?.(item.land_category_code) ?? null)
        : null;
  const head = NOT_A_CROP_KIND_LABEL[kind];
  return value ? `${head} · ${value}` : head;
}
